import { useMetadata } from '@api/useMetadata'
import { useOrders } from '@api/useOrders'
import { modes, paneModes } from '@constants/orderWizard'
import { useApi } from '@gravitate-js/the-armory'
import { getAPIURL } from '@utils/api'
import { isDefined } from '@utils/general'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'

import { addFlagsToSupply, createContainerStateObject } from '../../util'
import { useDestinationsContext } from '../DestinationsContext'
import { useSupplyOptionContext } from '../SupplyOptionContext'
import { useWizardMaster } from '../WizardMasterContext'
import { useProductGroups } from './ProductGroups/useProductGroups'

const OrderContainersContext = createContext()

export function OrderContainersContextProvider({ children }) {
  const {
    wizardOrder,
    setWizardOrder,
    setIsShortLoad,
    initializeSelectedStatus,
    setDriverNote,
    setOrderStops,
    setMode,
    paneMode,
    setPaneMode,
    setOrderEdited,
    setPayload,
    form,
    currentEntryMode,
  } = useWizardMaster()

  const { orderDestinations, setOrderDestinations, getImpactedTankVolumes, setSelectedTank, defaultStore } =
    useDestinationsContext()

  const [compartments, setCompartments] = useState([null, null, null])
  const [volumes, setVolumes] = useState([1000, 1000, 1000, 1000, 1000])
  const [maxVolumes, setMaxVolumes] = useState([])

  const [hasChangedTrailer, setHasChangedTrailer] = useState(false)
  const [trailerConfigPayload, setTrailerConfigPayload] = useState(0)
  const productGroupsApi = useProductGroups(wizardOrder)
  const api = useApi({ baseURLOverride: getAPIURL() })

  const productMaxVolume = useMemo(() => {
    if (currentEntryMode === 'by_product') return productGroupsApi.maximumVolume
    return (maxVolumes ?? []).reduce((acc, volume) => acc + volume, 0)
  })

  const productVolumeTotal = useMemo(() => {
    if (currentEntryMode === 'by_product') return productGroupsApi.volumeTotal
    if (!compartments) return 0
    return compartments
      .filter((compartment) => compartment !== null)
      .flatMap((compartment) => compartment.targets)
      .filter((target) => target && !!target?.volume)
      .reduce((acc, target) => acc + target?.volume, 0)
  })

  const { useDefaultCompartmentConfig, reattachCompartmentsMutation, swapTrailerMutation, trimCompartmentMutation } =
    useOrders()

  const { refetch: refetchDefaultConfig, data: compartmentConfigData } = useDefaultCompartmentConfig(
    trailerConfigPayload?.market,
    trailerConfigPayload?.first_store_number
  )

  const defaultTrailerConfig = useMemo(
    () => compartmentConfigData?.trailer_config,
    [compartmentConfigData?.trailer_config]
  )

  const getTrimmedDefaults = async () => {
    if (
      compartments.some((c) => c === null) ||
      !compartmentConfigData ||
      !compartments?.length ||
      !orderDestinations?.length ||
      !(compartments?.length === compartmentConfigData?.default?.length)
    ) {
      return []
    }

    const data = await trimCompartmentMutation.mutateAsync({
      compartments: compartments.map((c, ci) => ({
        ...c,
        volume: compartmentConfigData.default[ci],
        targets: c?.targets.map((t) => ({ ...t, volume: compartmentConfigData.default[ci] })),
      })),
      first_store_number: orderDestinations[0]?.store_number,
      market: orderDestinations[0].market,
      trailer_config: compartmentConfigData.trailer_config,
    })

    return data
  }

  const [trimmedDefaults, setTrimmedDefaults] = useState([])

  useEffect(() => {
    ;(async () => {
      const nullCompartments = compartments?.some((c) => c === null)
      if (!compartmentConfigData || nullCompartments) return
      const trimmedResult = await getTrimmedDefaults()
      setTrimmedDefaults(trimmedResult)
    })()
  }, [compartmentConfigData])

  const [trailerOverride, setTrailerOverride] = useState(defaultTrailerConfig)

  const { data: trailerMetadata } = useMetadata('trailer')
  const pocketsAllocated = useMemo(() => {
    const includesNull = compartments?.includes(null)
    const hasUnlinkedProduct = compartments?.some((t) => t?.store_number === 'N/A')
    const isErrored = compartments?.some((c) => c?.targets?.some((t) => t?.is_error))
    return !includesNull && !hasUnlinkedProduct && !isErrored
  }, [compartments])

  // Watch for changes in the default volumes (after trim) and see if the current compartments are still 'modified'
  useEffect(() => {
    if (!trimmedDefaults.length) return
    setCompartments((curr) =>
      curr.map((c, ci) => {
        return {
          ...c,
          targets: c?.targets?.map((t) => ({ ...t, modified: t.volume !== trimmedDefaults?.[ci]?.volume })),
        }
      })
    )
  }, [trimmedDefaults])

  const { setSelectedSupply, setSelectedStatus } = useSupplyOptionContext()

  useEffect(() => {
    const firstDestination = orderDestinations[0]

    if (firstDestination) {
      setTrailerConfigPayload({
        market: firstDestination.market,
        first_store_number: firstDestination.store_number,
      })
    } else if (defaultStore) {
      setTrailerConfigPayload({
        market: defaultStore.market,
        first_store_number: defaultStore.store_number,
      })
    }
  }, [orderDestinations[0], defaultStore])

  useEffect(() => {
    if (!wizardOrder && defaultTrailerConfig) {
      setTrailerOverride(defaultTrailerConfig)
      updateVolumes(compartmentConfigData, undefined, true)
    }
  }, [defaultTrailerConfig])

  // Use the trailer config from a pre-existing order when starting
  useEffect(() => {
    if (wizardOrder?.trailer_config) {
      setTrailerOverride(wizardOrder?.trailer_config)
    }
  }, [wizardOrder?.number, wizardOrder?.trailer_config])

  const handleSwapTrailer = async () => {
    const data = await swapTrailerMutation.mutateAsync({
      order_number: wizardOrder?.number,
      new_trailer_config_type: trailerOverride,
      save: false,
    })
    const orderData = data?.order
    setSelectedTank(undefined)
    setIsShortLoad(orderData?.is_short_load)
    constructCompartments(orderData)
    setSelectedSupply(addFlagsToSupply(orderData))
    initializeSelectedStatus(orderData, setSelectedStatus)
    setDriverNote(orderData.note?.content)
    form.setFieldsValue({ content: orderData.note?.content })
    setWizardOrder(orderData)
    setOrderStops(orderData?.map_stops)
    setPaneMode(paneModes.TANKIMPACT)
    setPayload(orderData)
    setOrderEdited(true)
    setMode(modes.EDIT)
  }

  useEffect(() => {
    if (trailerMetadata && trailerOverride) {
      const maxVolumeData = trailerMetadata?.max_volumes?.find((t) => t.configuration === trailerOverride)?.volumes
      if ((!!maxVolumeData?.length && !!maxVolumes) || !!maxVolumes?.length) {
        setMaxVolumes(maxVolumeData ?? [])
      }
    }
  }, [trailerMetadata, trailerOverride])

  useEffect(() => {
    if (!hasChangedTrailer) return
    if (!trailerMetadata) return
    // Lookup default volumes for new config
    const defaultVolumes = trailerMetadata?.default_volumes?.find((t) => t.configuration === trailerOverride)?.volumes
    const maxVolumeData = trailerMetadata?.max_volumes?.find((t) => t.configuration === trailerOverride)?.volumes
    if (defaultVolumes && maxVolumeData) {
      if (paneMode === paneModes.TANKIMPACT) {
        handleSwapTrailer()
      } else {
        const newCompartments = updateVolumes({ default: defaultVolumes, max: maxVolumeData })
        const shouldTrim = newCompartments.length <= compartments.length

        if (shouldTrim && newCompartments.every((c) => c !== null)) {
          const destination = orderDestinations[0]
          correctCompartmentVolumes(
            destination?.market,
            newCompartments,
            getImpactedTankVolumes,
            setOrderDestinations,
            destination?.store_number
          )
        }

        setOrderDestinations(getImpactedTankVolumes(newCompartments))
      }
    }
  }, [trailerOverride, trailerMetadata])

  const constructCompartments = (order) => {
    if (order) {
      const createdCompartments = createContainerStateObject(order)
      setCompartments(createdCompartments)
      const newVolumes = createdCompartments.map((compartment) => compartment.volume)
      setVolumes(newVolumes)
    }
  }

  const resetVolumes = async () => {
    const compartmentsCopy = [...compartments]
    if (trimmedDefaults?.length) {
      compartments.forEach((c, i) => {
        if (c) {
          c.targets[0].volume = trimmedDefaults[i].volume
          c.volume = trimmedDefaults[i].volume
          delete c.targets[0].modified
        }
      })
      setCompartments(compartmentsCopy)
      return
    }
    if (compartmentConfigData) {
      const newCompartments = compartmentsCopy.map((c, i) => {
        if (c) {
          c.targets[0].volume = compartmentConfigData.default[i]
          c.volume = compartmentConfigData.default[i]
          delete c.targets[0].modified
        }
        return c
      })
      setCompartments(newCompartments)
      return
    }
    console.error('No default compartment config data available')
  }

  const resetCompartments = () => {
    updateVolumes(compartmentConfigData, undefined, true)
  }

  const updateVolumes = (newVolumes, newCompartments, clearPockets) => {
    if (!newVolumes) return
    let compartmentCopy = newCompartments ? [...newCompartments] : [...compartments]
    if (clearPockets) compartmentCopy = []

    setVolumes(newVolumes.default)
    setMaxVolumes(newVolumes.max ?? [])

    const newLength = newVolumes.default.length

    if (newLength < compartmentCopy.length) {
      compartmentCopy.splice(newLength, compartmentCopy.length - newLength)
    } else {
      while (compartmentCopy.length < newLength) {
        compartmentCopy.push(null)
      }
    }

    compartmentCopy.forEach((c, i) => {
      if (c) {
        c.volume = newVolumes.default[i]
        c.targets = [{ ...c.targets[0], volume: newVolumes.default[i] }]
      }
    })

    setCompartments(compartmentCopy)
    return compartmentCopy
  }

  const addCompartmentTarget = (containerIndex, locationName, tankID) => {
    const compartmentsCopy = [...compartments]
    compartmentsCopy[containerIndex].targets.push({
      location_name: locationName,
      volume: 0,
      tank_id: tankID,
    })
    setCompartments(compartmentsCopy)
  }
  const removeTarget = (pocketIndex) => {
    const compartmentsCopy = [...compartments]
    compartmentsCopy[pocketIndex].targets.pop()
    setCompartments(compartmentsCopy)
  }
  const changeTargetLocation = (pocketIndex, targetIndex, locationName, tankId) => {
    const compartmentsCopy = [...compartments]
    compartmentsCopy[pocketIndex].targets[targetIndex].location_name = locationName
    compartmentsCopy[pocketIndex].targets[targetIndex].tank_id = tankId
    setCompartments(compartmentsCopy)
  }
  const changeVolume = async (
    value,
    containerIndex,
    targetIndex,
    setIsShortLoad,
    getImpactedTankVolumes,
    truckIsLoaded,
    wizardOrder
  ) => {
    if (value === '') return
    const compartmentsCopy = [...compartments]

    compartmentsCopy[containerIndex].targets[targetIndex].volume = value
    compartmentsCopy[containerIndex].targets[targetIndex].modified = value !== trimmedDefaults[containerIndex]?.volume

    if (compartmentsCopy[containerIndex].targets.length !== 1) {
      const totalMax = truckIsLoaded
        ? wizardOrder.compartments[containerIndex].targets.map((t) => t.volume).reduce((a, b) => a + b.volume)
        : maxVolumes[containerIndex]
      if (
        targetIndex === 0 &&
        compartmentsCopy[containerIndex].targets[0].volume + compartmentsCopy[containerIndex].targets[1].volume >
          totalMax
      ) {
        compartmentsCopy[containerIndex].targets[1].volume =
          totalMax - compartmentsCopy[containerIndex].targets[0].volume
      } else if (
        targetIndex === 1 &&
        compartmentsCopy[containerIndex].targets[0].volume + compartmentsCopy[containerIndex].targets[1].volume >
          totalMax
      ) {
        compartmentsCopy[containerIndex].targets[0].volume =
          totalMax - compartmentsCopy[containerIndex].targets[1].volume
      }
    }
    getImpactedTankVolumes(compartmentsCopy)
    setIsShortLoad(true)
    setCompartments(compartmentsCopy)
  }

  const replaceContainers = async (
    oldStoreNumber,
    newStore,
    destIndex,
    orderDestinations,
    getImpactedTankVolumes,
    setOrderDestinations
  ) => {
    const request = {
      old_store: oldStoreNumber,
      new_store: newStore.store_number,
      compartments,
      first_store_number: newStore.store_number,
    }

    const data = await reattachCompartmentsMutation.mutateAsync(request)

    if (data.some((d) => d && d.targets?.some((t) => t.is_error))) {
      data.forEach((t) => {
        if (t && t.is_error && t.store_number !== 'N/A') t.store_number = newStore.store_number
        if (t && t.store_number === 'N/A') t.is_error = true
      })
    }
    if (!data.includes(null)) {
      if (!data.some((d) => d && d.is_error)) {
      }
    }

    const latestDestinations = [...orderDestinations]
    latestDestinations[destIndex] = newStore

    if (destIndex === 0 && orderDestinations.length === 1) {
      // Trailer config won't update until the next render, so we're refetching the new defaults directly below
      setTrailerConfigPayload({
        market: newStore.market,
        first_store_number: latestDestinations[0]?.store_number,
      })

      // Normally we don't use the API directly, but in this case we need to beat a race condition with the change to the trailer config payload and the refetched config data
      const refetchedConfigData = await api.post('trailer/compartments/general', {
        market: newStore.market,
        first_store_number: latestDestinations[0]?.store_number,
      })

      if (refetchedConfigData) {
        const { default: def, max, trailer_config } = refetchedConfigData
        updateVolumes({ default: def, max }, data, false)
        setTrailerOverride(trailer_config)
      }
      setCompartments(data)
    }

    setOrderDestinations(getImpactedTankVolumes(data, latestDestinations))
  }
  const haveCompartmentsChanged = (oldCompartments) => {
    if (!oldCompartments?.length !== compartments?.length) return { hasStoreChanged: true } // Trailer config change
    if (!oldCompartments || !compartments) return { hasStoreChanged: true, hasProductChanged: true }

    let hasProductChanged = false
    let hasStoreChanged = false
    let hasVolumeChanged = false
    compartments.forEach((c, i) => {
      if (!c) return
      if (oldCompartments[i].product_name.toUpperCase() !== compartments[i].product_name.toUpperCase())
        hasProductChanged = true
      if (
        oldCompartments[i].targets[0].location_name.toUpperCase() !==
          compartments[i].targets[0].location_name.toUpperCase() ||
        compartments[i].targets.length !== oldCompartments[i].targets.length ||
        oldCompartments[i].targets[0].tank_id !== compartments[i].targets[0].tank_id
      )
        hasStoreChanged = true
      if (oldCompartments[i].targets[0].volume !== compartments[i].targets[0].volume) hasVolumeChanged = true
    })

    return { hasStoreChanged, hasProductChanged, hasVolumeChanged }
  }
  const checkTargetsForZero = () => {
    const response = { hasFirstTargetZero: false, hasSecondTargetZero: false, problemTargets: [] }
    compartments.forEach((c, i) => {
      c.targets.forEach((t, j) => {
        if (t.volume <= 0 || !t.volume) {
          if (j === 0) {
            response.hasFirstTargetZero = true
          } else {
            response.hasSecondTargetZero = true
            response.problemTargets.push([i])
          }
        }
      })
    })

    return response
  }
  const removeEmptyTargets = (problemTargets) => {
    const compartmentsCopy = [...compartments]
    problemTargets.forEach((c) => compartmentsCopy[c].targets.pop())
    setCompartments(compartmentsCopy)
  }
  const movePocketDestination = (pocketIndex, newStore, tankId, getImpactedTankVolumes, setOrderDestinations) => {
    const pocketCopy = [...compartments]
    pocketCopy[pocketIndex].targets[0].location_name = newStore
    pocketCopy[pocketIndex].targets[0].tank_id = tankId
    const sum = pocketCopy[pocketIndex].targets.map((target) => target.volume).reduce((a, b) => a + b)
    pocketCopy[pocketIndex].volume = sum
    pocketCopy[pocketIndex].targets[0].volume = sum
    pocketCopy[pocketIndex].targets[0].is_error = false
    setOrderDestinations(getImpactedTankVolumes(pocketCopy))

    setCompartments(pocketCopy)
  }
  const deletePocket = (index, getImpactedTankVolumes, setOrderDestinations) => {
    const compartmentsCopy = [...compartments]
    compartmentsCopy[index] = null
    setOrderDestinations(getImpactedTankVolumes(compartmentsCopy))
    setCompartments([...compartmentsCopy])
  }

  const correctCompartmentVolumes = async (
    market,
    compartments,
    getImpactedTankVolumes,
    setOrderDestinations,
    firstStoreNumber
  ) => {
    const trimmedCompartments = await trimCompartmentMutation.mutateAsync({
      compartments,
      market,
      first_store_number: firstStoreNumber,
      trailer_config: trailerOverride,
    })

    // Trimming should not override compartments that have been manually modified by the user (only in edit mode)
    if (!isDefined(wizardOrder)) {
      setCompartments(trimmedCompartments)
      setOrderDestinations(getImpactedTankVolumes(trimmedCompartments))
    } else {
      const withModifiedIgnored = trimmedCompartments.map((c, ci) => ({
        ...c,
        targets: c.targets.map((t, ti) => (compartments[ci].targets[ti].modified ? compartments[ci].targets[ti] : t)),
      }))

      setCompartments(withModifiedIgnored)
      setOrderDestinations(getImpactedTankVolumes(withModifiedIgnored))
    }
  }

  const createCompartment = (tank, storeNumber, firstEmptyIndex) => {
    return {
      product_name: tank.product,
      volume: volumes[firstEmptyIndex],
      targets: [
        {
          location_name: storeNumber,
          tank_id: tank.tank_id,
          volume: volumes[firstEmptyIndex],
        },
      ],

      store_number: storeNumber,
      compartment_index: firstEmptyIndex,
    }
  }

  const addProductToContainer = (
    tank,
    storeNumber,
    market,
    getImpactedTankVolumes,
    setOrderDestinations,
    firstStoreNumber
  ) => {
    const firstEmptyIndex = compartments.indexOf(null)

    if (firstEmptyIndex === -1) return
    compartments[firstEmptyIndex] = createCompartment(tank, storeNumber, firstEmptyIndex)
    if (!compartments.includes(null)) {
      correctCompartmentVolumes(market, compartments, getImpactedTankVolumes, setOrderDestinations, firstStoreNumber)
    } else {
      const newDestinations = getImpactedTankVolumes(compartments)
      setOrderDestinations(newDestinations)
    }
  }

  return (
    <OrderContainersContext.Provider
      value={{
        compartments,
        pocketsAllocated,
        volumes,
        maxVolumes,
        setMaxVolumes,
        constructCompartments,
        movePocketDestination,
        haveCompartmentsChanged,
        deletePocket,
        replaceContainers,
        addProductToContainer,
        updateVolumes,
        changeVolume,
        setTrailerConfigPayload,
        addCompartmentTarget,
        removeTarget,
        changeTargetLocation,
        removeEmptyTargets,
        checkTargetsForZero,
        setCompartments,
        setVolumes,
        resetVolumes,
        trailerMetadata,
        trailerOverride,
        setTrailerOverride,
        setHasChangedTrailer,
        resetCompartments,
        productVolumeTotal,
        productMaxVolume,
        productGroupsApi,
        setTrimmedDefaults,
      }}
    >
      {children}
    </OrderContainersContext.Provider>
  )
}

export const useOrderContainersContext = () => {
  const context = useContext(OrderContainersContext)
  if (context === undefined) {
    throw new Error('Context must be used within a Provider')
  }
  return context
}
